import { alpha, Box, ButtonBase, styled, Tooltip } from "@mui/material";
import { Paragraph, Span } from "components/Typography";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getNavigations } from "../layout-parts/navigation";
import SidebarAccordion from "./SidebarAccordion";
const SIDEBAR_ACTIVE_DARK = "#86efac"; // Light green for dark mode

const NavItemButton = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "navDisabled"
})(({
  theme,
  active,
  navDisabled
}) => ({
  height: 44,
  width: "100%",
  borderRadius: 8,
  marginBottom: 4,
  padding: "0 18px",
  justifyContent: "flex-start",
  transition: "all 0.15s ease",
  ...(active && !navDisabled && {
    color: theme.palette.mode === "dark" ? SIDEBAR_ACTIVE_DARK : theme.palette.primary.main,
    backgroundColor: theme.palette.mode === "dark"
      ? alpha(SIDEBAR_ACTIVE_DARK, 0.12)
      : alpha(theme.palette.primary.main, 0.06),
  }),
  ...(navDisabled && {
    opacity: 0.45,
    cursor: "not-allowed",
    color: theme.palette.text.disabled,
  }),
  "&:hover": {
    backgroundColor: navDisabled ? "transparent" : theme.palette.action.hover
  }
}));
const ListLabel = styled(Paragraph)(({
  theme,
  compact
}) => ({
  fontWeight: 700,
  fontSize: "12px",
  marginTop: "20px",
  marginLeft: "15px",
  marginBottom: "10px",
  textTransform: "uppercase",
  transition: "all 0.15s ease",
  color: theme.palette.text.secondary,
  ...(compact && {
    opacity: 0,
    width: 0
  })
}));
const ExternalLink = styled("a")(({
  theme
}) => ({
  overflow: "hidden",
  whiteSpace: "pre",
  marginBottom: "8px",
  textDecoration: "none",
  color: theme.palette.text.primary
}));
const StyledText = styled(Span, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "compact" && prop !== "navDisabled"
})(({
  theme,
  compact,
  active,
  navDisabled
}) => ({
  whiteSpace: "nowrap",
  paddingLeft: "0.8rem",
  transition: "all 0.15s ease",
  fontSize: "13px",
  fontWeight: 650,
  color: navDisabled
    ? theme.palette.text.disabled
    : active
      ? (theme.palette.mode === "dark" ? SIDEBAR_ACTIVE_DARK : theme.palette.primary.main)
      : theme.palette.text.secondary,
  ...(compact && {
    opacity: 0,
    width: 0
  })
}));
const BulletIcon = styled("div", {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "navDisabled"
})(({
  theme,
  active,
  navDisabled
}) => ({
  width: 4,
  height: 4,
  marginLeft: "10px",
  marginRight: "8px",
  overflow: "hidden",
  borderRadius: "50%",
  background: navDisabled
    ? theme.palette.text.disabled
    : active
      ? (theme.palette.mode === "dark" ? SIDEBAR_ACTIVE_DARK : theme.palette.primary.main)
      : theme.palette.text.disabled,
  boxShadow: active && !navDisabled
    ? (theme.palette.mode === "dark"
        ? `0px 0px 0px 3px ${alpha(SIDEBAR_ACTIVE_DARK, 0.3)}`
        : `0px 0px 0px 3px ${theme.palette.primary[200]}`)
    : "none"
}));
const BadgeValue = styled(Box)(({
  compact,
  theme
}) => ({
  color: "white",
  fontSize: "12px",
  fontWeight: 500,
  padding: "1px 6px",
  overflow: "hidden",
  borderRadius: "300px",
  backgroundColor: theme.palette.primary.main,
  display: compact ? "none" : "unset"
})); // Common icon style

const iconStyle = (active, navDisabled) => (theme) => ({
  fontSize: 18,
  marginRight: "4px",
  color: navDisabled
    ? "text.disabled"
    : active
      ? (theme.palette.mode === "dark" ? SIDEBAR_ACTIVE_DARK : "primary.main")
      : "text.secondary"
}); // ---------------------------------------------------------------


// ---------------------------------------------------------------
const MultiLevelMenu = ({
  sidebarCompact
}) => {
  const navigate = useNavigate();
  const {
    pathname
  } = useLocation();
  const [navVersion, setNavVersion] = useState(0);

  // Re-read navigations when permissions are saved for the current user
  useEffect(() => {
    const refreshNav = () => setNavVersion((v) => v + 1);
    window.addEventListener("permissions-updated", refreshNav);
    window.addEventListener("storage", refreshNav);
    return () => {
      window.removeEventListener("permissions-updated", refreshNav);
      window.removeEventListener("storage", refreshNav);
    };
  }, []);

  const activeRoute = path => pathname === path ? 1 : 0;


  const handleNavigation = path => navigate(path);


  const COMPACT = sidebarCompact ? 1 : 0;
  // navVersion triggers a re-render so getNavigations() re-reads localStorage
  void navVersion;
  const navigations = getNavigations();

  const renderLevels = data => {
    return data.map((item, index) => {
      if (item.type === "label") return <ListLabel key={index} compact={COMPACT}>
            {item.label}
          </ListLabel>;

      if (item.children) {
        return <SidebarAccordion key={index} item={item} sidebarCompact={COMPACT}>
            {renderLevels(item.children)}
          </SidebarAccordion>;
      } else if (item.type === "extLink") {
        return <ExternalLink key={index} href={item.path} rel="noopener noreferrer" target="_blank">
            <NavItemButton key={item.name} name="child" active={0}>
              {(() => {
              if (item.icon) {
                return <item.icon sx={iconStyle(0)} />;
              } else {
                return <span className="item-icon icon-text">{item.iconText}</span>;
              }
            })()}

              <StyledText compact={COMPACT} active={activeRoute(item.path)}>
                {item.name}
              </StyledText>

              <Box mx="auto" />

              {item.badge && <BadgeValue compact={COMPACT}>{item.badge.value}</BadgeValue>}
            </NavItemButton>
          </ExternalLink>;
      } else {
        const isDisabled = !!item.disabled;
        const isActive = activeRoute(item.path);

        const navButton = (
          <NavItemButton
            key={item.name}
            className="navItem"
            active={isActive}
            navDisabled={isDisabled}
            disabled={isDisabled}
            aria-disabled={isDisabled}
            onClick={() => {
              if (!isDisabled) handleNavigation(item.path);
            }}
          >
            {item?.icon ? (
              <item.icon sx={iconStyle(isActive, isDisabled)} />
            ) : (
              <BulletIcon active={isActive} navDisabled={isDisabled} />
            )}

            <StyledText compact={COMPACT} active={isActive} navDisabled={isDisabled}>
              {item.name}
            </StyledText>

            <Box mx="auto" />

            {item.badge && <BadgeValue compact={COMPACT}>{item.badge.value}</BadgeValue>}
          </NavItemButton>
        );

        return (
          <Box key={index}>
            {isDisabled ? (
              <Tooltip title="You don't have access to this module" placement="right" arrow>
                <Box component="span" sx={{ display: "block", width: "100%" }}>
                  {navButton}
                </Box>
              </Tooltip>
            ) : (
              navButton
            )}
          </Box>
        );
      }
    });
  };

  return <>{renderLevels(navigations)}</>;
};

export default MultiLevelMenu;
